import Card from '@material-ui/core/Card';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import * as moment from 'moment';
import * as React from 'react';
import { Line } from 'react-chartjs-2';
import { stylesheet } from "typestyle";
import { ChartData, Logger, Reading } from './Models';

const css = stylesheet({
	card: {
		height: '60vh',
		position: 'relative',
		width: '100%',
	}
})

// The chart configuration (without the data)
const config: Chart.ChartOptions = {
	animation: {
		duration: 0
	},
	legend: {
		display: true,
		labels: {
			fontColor: 'rgb(0, 0, 0)',
			usePointStyle: true,
		}
	},
	maintainAspectRatio: false,
	responsive: true,
	scales: {
		xAxes: [{
			display: true,
			distribution: 'linear',
			scaleLabel: {
				display: true,
				fontColor: 'rgb(0, 0, 0)',
				labelString: 'Time'
			},
			ticks: {
				fontColor: 'rgb(0, 0, 0)'
			},
			time: {
				displayFormats: {
					day: 'D. MMM',
					hour: 'HH:00',
					minute: 'HH:mm',
					second: 'HH:mm:ss',
				},
				max: moment().add(1, 'seconds').toISOString(),
				unit: 'second',
			},
			type: 'time',
		}],
		yAxes: [{
			display: true,
			gridLines: {
				color: 'rgb(255, 204, 204)',
			},
			id: 'y-axis-oxygen',
			scaleLabel: {
				display: true,
				fontColor: 'rgb(102, 0, 0)',
				labelString: 'Oxygen'
			},
			ticks: {
				fontColor: 'rgb(102, 0, 0)',
				max: 150,
				min: 40,
			}
		}, {
			display: true,
			gridLines: {
				color: 'rgb(153, 255, 204)',
			},
			id: 'y-axis-depth',
			position: 'right',
			scaleLabel: {
				display: true,
				fontColor: 'rgb(0, 102, 0)',
				labelString: 'Depth in meters'
			},
			ticks: {
				fontColor: 'rgb(0, 102, 0)',

				max: 0,
				min: -40,
			},
		}]
	},
	tooltips: {
		enabled: false,
		intersect: false,
		mode: 'x',
		position: 'nearest'
	},
};

@observer
export default class TimeSeriesChart extends React.Component<{
	loggers: Array<Logger>,
	chartData: ChartData,
}>{

	render() {
		const { loggers } = this.props;
		const chartData = toJS(this.props.chartData)
		const time = config.scales!.xAxes![0]!.time!
		const hasOxygen = chartData.oxygen.length > 0
		const hasDepth = chartData.depth.length > 0

		const dataForDateMapping: Array<Reading> = hasOxygen ? chartData.oxygen
			: hasDepth ? chartData.depth
				: new Array<Reading>()


		time.max = moment().add(1, 'seconds').toISOString();
		const mindate = moment.min(dataForDateMapping.map(r => r.x));
		if (mindate) {
			if (mindate.isBefore(moment().subtract(7, 'days'))) {
				time.unit = 'day';
			}
			else if (mindate.isBefore(moment().subtract(24, 'hours'))) {
				time.unit = 'minute';
			}
			else if (mindate.isBefore(moment().subtract(5, 'minutes'))) {
				time.unit = 'minute';
			}
		}

		// Build datasets with oxygen/depth/temperature where available
		const datasets: Array<Chart.ChartDataSets> = new Array<Chart.ChartDataSets>()
		loggers.forEach(logger => {

			config.scales!.yAxes!
				.find(axe => axe.id === 'y-axis-oxygen')!
				.display = hasOxygen
			if (hasOxygen) {
				const data: any = chartData.oxygen
					.filter(reading => reading.loggerId === logger.id)
					.map(reading => ({
						loggerId: reading.loggerId,
						x: reading.x,
						y: reading.y,
					}))
				datasets.push({
					borderColor: logger.colour,
					data,
					fill: false,
					label: `${logger.chartLabel} (O\u2082)`,
					pointHoverRadius: 0,
					pointRadius: 1,
					spanGaps: true,
					yAxisID: 'y-axis-oxygen',
				})
			}

			config.scales!.yAxes!
				.find(axe => axe.id === 'y-axis-depth')!
				.display = hasDepth
			if (hasDepth) {
				const data: any = chartData.depth
					.filter(reading => reading.loggerId === logger.id)
					.map(reading => ({
						loggerId: reading.loggerId,
						x: reading.x,
						y: reading.y,
					}))
				datasets.push({
					borderColor: logger.colour,
					borderDash: [5, 5],
					data,
					fill: false,
					label: `${logger.chartLabel} (m)`,
					pointHoverRadius: 0,
					pointRadius: 1,
					spanGaps: true,
					yAxisID: 'y-axis-depth',

				})
			}

		})

		const myData: Chart.ChartData = {
			datasets
		}

		return (
			<Card className={css.card}>
				<Line
					data={myData}
					options={config}
				/>
			</Card>
		)
	}
}


// WEBPACK FOOTER //
// ./src/TimeSeriesChart.tsx